import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, User } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES } from "@/lib/api";
import useSettings from "@/hooks/useSettings";
import { COLLECTIONS } from "@/lib/collections";
import useUiTexts, { uiText } from "@/lib/uiTexts";

const NAV_ALL = [
  { to: "/the-archive", label: "the archive" },
  { to: "/the-essentials", label: "the essentials" },
  { to: "/made-to-order", label: "made to order" },
  { to: "/archivo", label: "todas las piezas" },
  { to: "/camila-guerra", label: "Camila Guerra" },
  { to: "/mi-archivo", label: "Mi Archivo" },
];

export const Layout = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);
  const { count } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const settings = useSettings();
  const T = useUiTexts();
  const isPrelaunch = settings.sales_stage === "exhibicion";
  const NAV = NAV_ALL.filter((n) => n.to !== "/camila-guerra" || settings.creator_enabled);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (y > lastY.current && y > 120) setHidden(true);
      else if (y < lastY.current - 4 || y <= 120) setHidden(false);
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen flex flex-col relative">
      <div aria-hidden className="studio-backdrop fixed inset-0 -z-10 pointer-events-none" />
      <header className={`sticky top-0 z-50 backdrop-blur-xl bg-[#D9DADD]/85 border-b border-[#C9CBD1] transition-transform duration-300 ease-out ${hidden && !open ? "-translate-y-full" : "translate-y-0"}`}>
        <div className="px-4 sm:px-8 lg:px-16 pt-3 lg:pt-4 grid grid-cols-[1fr_auto_1fr] items-center">
          <div className="flex items-center">
            <button data-testid="nav-mobile-toggle" aria-label="Menú" onClick={() => setOpen(!open)} className="lg:hidden text-[#171719]">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
          <Logo size="text-3xl sm:text-4xl lg:text-5xl" />
          <div className="flex items-center justify-end gap-5">
            {settings.sales_stage !== "exhibicion" && (
              <button data-testid="nav-cart" aria-label="Carrito" onClick={() => navigate("/carrito")} className="relative text-[#171719] hover:text-[#565A61] transition-colors">
                <ShoppingBag size={20} />
                {count > 0 && (
                  <span data-testid="cart-count" className="absolute -top-2 -right-2 bg-[#171719] text-[#F4F4F3] text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{count}</span>
                )}
              </button>
            )}
            <button data-testid="nav-account" aria-label="Cuenta" onClick={() => navigate(user ? "/mi-archivo" : "/ingresar")} className="hidden sm:block text-[#171719] hover:text-[#565A61] transition-colors">
              <User size={20} />
            </button>
          </div>
        </div>
        <nav className="hidden lg:flex items-center justify-center gap-7 pb-3 pt-2">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`nav-${n.to.replace(/\//g, "")}`}
              className={`dossier-label hover:text-[#171719] transition-colors ${loc.pathname === n.to ? "text-[#171719]" : "text-[#565A61]"}`}>
              {n.label}
            </Link>
          ))}
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin" className="dossier-label text-[#565A61] hover:text-[#171719] transition-colors">Panel</Link>
          )}
        </nav>
        {open && (
          <div className="lg:hidden border-t border-[#C9CBD1] px-4 py-4 flex flex-col gap-4 bg-[#D9DADD]">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`mnav-${n.to.replace(/\//g, "")}`} className="dossier-label text-[#565A61]">{n.label}</Link>
            ))}
            {user?.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="dossier-label text-[#565A61]">Panel</Link>}
            <Link to={user ? "/mi-archivo" : "/ingresar"} onClick={() => setOpen(false)} className="dossier-label text-[#565A61]">{user ? "Mi cuenta" : "Ingresar"}</Link>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-[#C9CBD1] mt-24 px-4 sm:px-8 lg:px-16 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <Logo size="text-3xl" />
            <p className="font-script text-2xl text-[#565A61] mt-4 max-w-md">{uiText(T, "footer_tagline")}</p>
            <p className="text-sm text-[#565A61] mt-4 max-w-sm leading-relaxed">{uiText(T, "footer_about")}</p>
          </div>
          <div>
            <div className="dossier-label text-[#565A61] mb-4">Archivo</div>
            <div className="flex flex-col gap-2">
              <Link to="/archivo" className="text-sm text-[#565A61] hover:text-[#171719] transition-colors">Todas las piezas</Link>
              {COLLECTIONS.map((c) => (
                <Link key={c.key} to={c.to} className="text-sm text-[#565A61] hover:text-[#171719] transition-colors">{c.label}</Link>
              ))}
              {settings.creator_enabled && <Link to="/camila-guerra" data-testid="footer-vip-link" className="text-sm text-[#565A61] hover:text-[#171719] transition-colors">Cami Guerra Archivo VIP</Link>}
            </div>
          </div>
          <div>
            <div className="dossier-label text-[#565A61] mb-4">Marca</div>
            <div className="flex flex-col gap-2">
              <Link to="/contacto" className="text-sm text-[#565A61] hover:text-[#171719] transition-colors">Contacto</Link>
              <Link to="/legal/privacidad" className="text-sm text-[#565A61] hover:text-[#171719] transition-colors">Privacidad</Link>
              <Link to="/legal/condiciones" className="text-sm text-[#565A61] hover:text-[#171719] transition-colors">Condiciones de compra</Link>
              <Link to="/legal/cambios" className="text-sm text-[#565A61] hover:text-[#171719] transition-colors">Cambios y devoluciones</Link>
            </div>
          </div>
        </div>
        <div className="mt-12 pt-6 border-t border-[#C9CBD1] flex flex-col sm:flex-row justify-between gap-3">
          <span className="dossier-label text-[#565A61]">{uiText(T, "footer_copyright").replace("{año}", new Date().getFullYear())}</span>
          <span className="dossier-label text-[#565A61]">{uiText(T, "footer_credit")}</span>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
